import { doc, getDocFromServer, setDoc } from 'firebase/firestore'
import { assertFirestoreEnvironmentForWrite, firestore } from './firebase'

export interface MergeRule {
  main: string
  keys: string[]
}

export interface DeviceTypeNormalization {
  deviceType: string
  stateRules: MergeRule[]
  sensorDataRules: MergeRule[]
  updatedAt?: string
}

export interface GlobalDeviceTypeNormalization {
  scope: 'global'
  stateRules: MergeRule[]
  sensorDataRules: MergeRule[]
  updatedAt?: string
}

const LOCAL_STORAGE_PREFIX = 'utm_device_type_normalization_'
const GLOBAL_NORMALIZATION_STORAGE_KEY = `${LOCAL_STORAGE_PREFIX}__global`

function sortUnique(values: string[]): string[] {
  return Array.from(new Set(values.map((v) => String(v || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b))
}

function normalizeRule(rule: MergeRule): MergeRule {
  const keys = sortUnique(rule.keys || [])
  const explicitMain = String(rule.main || '').trim()
  const main = explicitMain || keys[0] || ''
  if (!main) return { main: '', keys: [] }
  // Keep keys as observed aliases; do not force main into keys.
  if (keys.length === 0) return { main, keys: [main] }
  return { main, keys }
}

function getDistinctNormalizedTokens(values: string[]): string[] {
  return Array.from(
    new Set(
      values
        .map((value) => normalizeMergeToken(value))
        .filter(Boolean)
    )
  )
}

function isNoopMergeRule(rule: MergeRule): boolean {
  const mainToken = normalizeMergeToken(rule.main)
  if (!mainToken) return true
  const keyTokens = getDistinctNormalizedTokens(rule.keys || [])
  if (keyTokens.length === 0) return true
  return keyTokens.length === 1 && keyTokens[0] === mainToken
}

function normalizeDoc(doc: DeviceTypeNormalization): DeviceTypeNormalization {
  return {
    deviceType: doc.deviceType,
    stateRules: (doc.stateRules || []).map(normalizeRule).filter((r) => r.main && !isNoopMergeRule(r)),
    sensorDataRules: (doc.sensorDataRules || []).map(normalizeRule).filter((r) => r.main && !isNoopMergeRule(r)),
    updatedAt: doc.updatedAt,
  }
}

function normalizeMergeToken(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function parseLocalGlobalNormalization(): GlobalDeviceTypeNormalization | null {
  const raw = localStorage.getItem(GLOBAL_NORMALIZATION_STORAGE_KEY)
  if (!raw) return null
  try {
    const data = JSON.parse(raw) as GlobalDeviceTypeNormalization
    return {
      scope: 'global',
      stateRules: (data.stateRules || []).map(normalizeRule).filter((r) => r.main && !isNoopMergeRule(r)),
      sensorDataRules: (data.sensorDataRules || []).map(normalizeRule).filter((r) => r.main && !isNoopMergeRule(r)),
      updatedAt: data.updatedAt,
    }
  } catch {
    return null
  }
}

function localStorageKey(deviceType: string) {
  return `${LOCAL_STORAGE_PREFIX}${deviceType}`
}

export async function loadDeviceTypeNormalization(deviceType: string): Promise<DeviceTypeNormalization> {
  if (!deviceType) {
    return { deviceType: '', stateRules: [], sensorDataRules: [] }
  }

  if (!firestore) {
    const raw = localStorage.getItem(localStorageKey(deviceType))
    if (!raw) return { deviceType, stateRules: [], sensorDataRules: [] }
    try {
      return normalizeDoc(JSON.parse(raw) as DeviceTypeNormalization)
    } catch {
      return { deviceType, stateRules: [], sensorDataRules: [] }
    }
  }

  const ref = doc(firestore, 'appfactory', 'config', 'deviceTypeNormalizations', deviceType)
  const snap = await getDocFromServer(ref)
  if (!snap.exists()) {
    return { deviceType, stateRules: [], sensorDataRules: [] }
  }
  const data = snap.data() as DeviceTypeNormalization
  return normalizeDoc({
    deviceType,
    stateRules: data.stateRules || [],
    sensorDataRules: data.sensorDataRules || [],
    updatedAt: data.updatedAt,
  })
}

export async function saveDeviceTypeNormalization(
  deviceType: string,
  payload: DeviceTypeNormalization
): Promise<DeviceTypeNormalization> {
  assertFirestoreEnvironmentForWrite('saveDeviceTypeNormalization')
  const normalized = normalizeDoc({
    ...payload,
    deviceType,
    updatedAt: new Date().toISOString(),
  })

  if (!firestore) {
    localStorage.setItem(localStorageKey(deviceType), JSON.stringify(normalized))
    return normalized
  }

  const ref = doc(firestore, 'appfactory', 'config', 'deviceTypeNormalizations', deviceType)
  await setDoc(ref, normalized, { merge: false })
  return normalized
}

export async function loadGlobalNormalization(): Promise<GlobalDeviceTypeNormalization> {
  const fallback: GlobalDeviceTypeNormalization = {
    scope: 'global',
    stateRules: [],
    sensorDataRules: [],
  }
  if (!firestore) {
    return parseLocalGlobalNormalization() || fallback
  }

  const localFallback = parseLocalGlobalNormalization()
  try {
    const ref = doc(firestore, 'appfactory', 'config', 'deviceTypeNormalizations', '__global')
    const snap = await getDocFromServer(ref)
    if (!snap.exists()) return localFallback || fallback
    const data = snap.data() as Partial<GlobalDeviceTypeNormalization>
    const normalized: GlobalDeviceTypeNormalization = {
      scope: 'global',
      stateRules: (data.stateRules || []).map(normalizeRule).filter((r) => r.main && !isNoopMergeRule(r)),
      sensorDataRules: (data.sensorDataRules || []).map(normalizeRule).filter((r) => r.main && !isNoopMergeRule(r)),
      updatedAt: data.updatedAt,
    }
    localStorage.setItem(GLOBAL_NORMALIZATION_STORAGE_KEY, JSON.stringify(normalized))
    return normalized
  } catch {
    return localFallback || fallback
  }
}

/**
 * Applies merge rules to raw keys: aliases are merged into their main key.
 * Returns merged (deduplicated) list of main keys.
 */
export function applyMergeRules(rawKeys: string[], rules: MergeRule[]): string[] {
  const safeRawKeys = Array.isArray(rawKeys) ? rawKeys.filter((x): x is string => typeof x === 'string') : []
  const safeRules = Array.isArray(rules) ? rules : []
  const rawTokenByKey = new Map<string, string>()
  safeRawKeys.forEach((key) => {
    const token = normalizeMergeToken(key)
    if (!token || rawTokenByKey.has(key)) return
    rawTokenByKey.set(key, token)
  })
  const rawTokenSet = new Set(Array.from(rawTokenByKey.values()))
  const consumed = new Set<string>()
  const consumedTokens = new Set<string>()
  const merged = new Set<string>()

  for (const rule of safeRules) {
    const mainToken = normalizeMergeToken(rule.main)
    const ruleTokenSet = new Set(
      (rule.keys || [])
        .map((key) => normalizeMergeToken(key))
        .filter(Boolean)
    )
    if (mainToken && ruleTokenSet.size === 1 && ruleTokenSet.has(mainToken)) continue
    const presentTokens = Array.from(ruleTokenSet).filter((token) => rawTokenSet.has(token))
    if (presentTokens.length === 0) continue
    const hasNewToken = presentTokens.some((token) => !consumedTokens.has(token))
    if (!hasNewToken) continue
    for (const [rawKey, token] of rawTokenByKey.entries()) {
      if (ruleTokenSet.has(token)) consumed.add(rawKey)
    }
    presentTokens.forEach((token) => consumedTokens.add(token))
    merged.add(rule.main)
  }

  safeRawKeys.forEach((key) => {
    if (!consumed.has(key)) merged.add(key)
  })
  return Array.from(merged).sort((a, b) => a.localeCompare(b))
}

/**
 * Converts raw keys (e.g. from stored enabledStates) to merged keys for UI display.
 * Used when loading preset: which merged keys should appear checked.
 */
export function rawKeysToMergedKeys(rawKeys: string[], rules: MergeRule[]): string[] {
  const safeRawKeys = Array.isArray(rawKeys) ? rawKeys.filter((x): x is string => typeof x === 'string') : []
  const safeRules = Array.isArray(rules) ? rules : []
  const merged = new Set<string>()

  for (const raw of safeRawKeys) {
    const rawToken = normalizeMergeToken(raw)
    const rule = safeRules.find((r) => {
      if (normalizeMergeToken(r.main) === rawToken) return true
      return (r.keys || []).some((key) => normalizeMergeToken(key) === rawToken)
    })
    merged.add(rule ? rule.main : raw)
  }
  return Array.from(merged).sort((a, b) => a.localeCompare(b))
}

/**
 * Expands merged keys to all raw keys (main + aliases) for runtime filtering.
 * Stored enabledStates use this so device state keys (e.g. fan_speed) match.
 */
export function expandMergedKeysToRaw(mergedKeys: string[], rules: MergeRule[]): string[] {
  const safeMerged = Array.isArray(mergedKeys) ? mergedKeys.filter((x): x is string => typeof x === 'string') : []
  const safeRules = Array.isArray(rules) ? rules : []
  const raw = new Set<string>()

  for (const m of safeMerged) {
    raw.add(m)
    const mergedToken = normalizeMergeToken(m)
    const rule = safeRules.find((r) => normalizeMergeToken(r.main) === mergedToken)
    if (rule) {
      ;(rule.keys || []).forEach((k) => raw.add(k))
    }
  }
  return Array.from(raw).sort((a, b) => a.localeCompare(b))
}

export async function saveGlobalNormalization(
  payload: GlobalDeviceTypeNormalization
): Promise<GlobalDeviceTypeNormalization> {
  assertFirestoreEnvironmentForWrite('saveGlobalNormalization')
  const normalized: GlobalDeviceTypeNormalization = {
    scope: 'global',
    stateRules: (payload.stateRules || []).map(normalizeRule).filter((r) => r.main && !isNoopMergeRule(r)),
    sensorDataRules: (payload.sensorDataRules || []).map(normalizeRule).filter((r) => r.main && !isNoopMergeRule(r)),
    updatedAt: new Date().toISOString(),
  }

  if (!firestore) {
    localStorage.setItem(GLOBAL_NORMALIZATION_STORAGE_KEY, JSON.stringify(normalized))
    return normalized
  }

  const ref = doc(firestore, 'appfactory', 'config', 'deviceTypeNormalizations', '__global')
  await setDoc(ref, normalized, { merge: false })
  localStorage.setItem(GLOBAL_NORMALIZATION_STORAGE_KEY, JSON.stringify(normalized))
  return normalized
}
